import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { StyledBox } from './styles'
import SurveyTitle from './SurveyTitle'
import { useAppDispatch } from '../../store'
import { addSection, setEditableSurvey, resetState, setTitle, setDraftSurvey, createInitialSectionWithSetId } from '../../store/slices/SurveyTemplateSlice'
import { ButtonType } from '../../components/GenericButton/styles'
import AddIcon from '@mui/icons-material/Add'
import GenericButton from '../../components/GenericButton'
import useAsyncEffect from 'use-async-effect'
import { useParams } from 'react-router-dom'
import { getSurveyByIdApi } from '../../store/slices/SurveyTemplateSlice/apis'
import { getEditableSurvey, getLoading, getSurveySections, getSurveyTitle } from '../../store/slices/SurveyTemplateSlice/selectors'
import SurveySections from './SurveySections'
import SurveyHeader from './SurveyHeader'
import PageHeader from '../../components/PageHeader'
import LoadingPageV2 from '../../pages/LoadingPageV2'
import PAGE_TITLES from '../../consts/titles'
import UnsavedBlockerModal from '../../components/UnsavedBlockerModal'
import { notification } from '../../components/Notifications'
import { isEqual } from 'lodash'
import { canPersistTemplateDraft, getTemplateDraftStoreKey } from './draftPersistence'

const SurveyTemplateFeature: React.FC = (): JSX.Element => {
  const dispatch = useAppDispatch()
  const loading = getLoading()
  const { id } = useParams()
  const title = getSurveyTitle()
  const sections = getSurveySections()
  const editableSurvey = getEditableSurvey()
  const storeKey = getTemplateDraftStoreKey(id)
  const [isDirty, setIsDirty] = useState(true)
  const initialLoadComplete = useRef(false)
  const loadedTemplateIdRef = useRef<string | undefined>(undefined)

  useLayoutEffect(() => {
    initialLoadComplete.current = false
    loadedTemplateIdRef.current = undefined
  }, [id])

  const setSurveyByApi = async (): Promise<void> => {
    if (id) {
      const survey = await dispatch(getSurveyByIdApi({ urlId: id })).unwrap()
      dispatch(setEditableSurvey(survey))
    }
  }

  // compare original survey with current state for dirtiness
  useEffect(() => {
    const isTitleDirty = title !== (editableSurvey?.title ?? '')
    const isSectionsDirty = !isEqual(
      sections,
      editableSurvey?.surveySections ?? [createInitialSectionWithSetId(sections?.[0]?.surveySectionId)]
    )
    setIsDirty(isTitleDirty || isSectionsDirty)
  }, [title, sections, editableSurvey])

  // locally store new title and sections when updated
  useEffect(() => {
    if (!canPersistTemplateDraft(initialLoadComplete.current, loadedTemplateIdRef.current, id)) return
    if (!isDirty) {
      localStorage.removeItem(storeKey)
      return
    }
    localStorage.setItem(storeKey, JSON.stringify({ title, sections }))
  }, [title, sections, isDirty, storeKey, id])

  const setSurveyByLocalStorage = (draft: string | null): void => {
    if (!draft) return
    setIsDirty(true)
    const parsed = JSON.parse(draft)
    dispatch(setTitle(parsed.title ?? title))
    dispatch(setDraftSurvey({ title: parsed.title ?? title, surveySections: parsed.sections ?? sections }))
    notification('Previous session ended without saving. Draft has been reloaded.', { variant: 'info' })
  }

  useAsyncEffect(async () => {
    initialLoadComplete.current = false
    loadedTemplateIdRef.current = undefined
    dispatch(resetState())

    const draft = localStorage.getItem(storeKey)
    await setSurveyByApi()
    setSurveyByLocalStorage(draft)
    loadedTemplateIdRef.current = id
    initialLoadComplete.current = true
  }, [id])

  const handleSection = (): void => {
    dispatch(addSection())
  }

  if (loading) {
    return <LoadingPageV2 title={PAGE_TITLES.SURVEY_TEMPLATE} />
  }
  return (
    <>
      <PageHeader
        title={title || PAGE_TITLES.UNTITLED_SURVEY_TEMPLATE}
        controls={<SurveyHeader setIsDirty={setIsDirty} isDirty={isDirty} />}
        showBackButton isCompactOnly
      />
      <StyledBox>
        <SurveyTitle />
        <SurveySections />
        <GenericButton
          buttonType={ButtonType.Secondary}
          startIcon={<AddIcon />}
          label='Add Section'
          fontWeight='bold'
          onClick={handleSection}
        />
        <UnsavedBlockerModal isDirty={isDirty} storeKey={storeKey} />
      </StyledBox>
    </>
  )
}

export default SurveyTemplateFeature
